'use client';

import { useEffect, useRef, useState } from 'react';
import type { SandboxedHtmlProps } from 'elowen-plugin-ui-kit';
import { htmlFrameCsp } from '../../lib/htmlFrameCsp';

/** Link targets a preview keeps. The frame shares the app's origin so the host can size it, so any other
 *  scheme (javascript:, data:, blob:, …) is removed rather than left for the sandbox and CSP alone. */
const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** A media query list as a light device evaluates it. The frame is light paper, but Chromium evaluates a
 *  frame's `prefers-color-scheme` from the device preference, whatever the frame element's `color-scheme`,
 *  so a mail's dark-mode rules (white copy for a dark client) would apply over the white page. `media` is
 *  the browser's own serialization, in which the feature always reads `(prefers-color-scheme: dark)`;
 *  it is replaced by a condition with the same truth value on a light device (a width is never negative). */
function lightMedia(media: string): string {
  return media.replaceAll('(prefers-color-scheme: dark)', '(width < 0px)').replaceAll('(prefers-color-scheme: light)', '(width >= 0px)');
}

/** Rewrites dark-mode conditions and drops `color-scheme` declarations (`:root { color-scheme: light dark }`
 *  would otherwise turn the page dark on a dark device). Returns whether any rule changed. */
function lightRules(rules: CSSRuleList): boolean {
  let changed = false;
  for (const rule of rules) {
    if (rule instanceof CSSMediaRule) {
      const media = lightMedia(rule.media.mediaText);
      if (media !== rule.media.mediaText) {
        rule.media.mediaText = media;
        changed = true;
      }
    }
    if (rule instanceof CSSStyleRule && rule.style.getPropertyValue('color-scheme') !== '') {
      rule.style.removeProperty('color-scheme');
      changed = true;
    }
    if (rule instanceof CSSGroupingRule && lightRules(rule.cssRules)) changed = true;
  }
  return changed;
}

/** A `<style>` element's text is raw: when the document is serialized for `srcdoc` and parsed again, a `</`
 *  followed by `style` ends the element there and the rest of the sheet becomes markup the sanitizer never
 *  saw. CSSOM writes an escaped `<` back literally inside a string (`"\3c/style\3e"` becomes `"</style>"`)
 *  and WebKit writes one as `\<` in a custom property, so every `<` that starts `</`, escaped or not, is
 *  written as the escape `\3c ` again. A string or identifier keeps its value; a bare `<` delimiter
 *  followed by `/` means nothing in any property. Every other `<`, such as range media syntax, stays. */
function rawTextCss(css: string): string {
  let text = '';
  for (let index = 0; index < css.length; index++) {
    // An escape is one unit, so `\\` is never read as the start of `\<`.
    const length = css[index] === '\\' ? 2 : 1;
    const unit = css.slice(index, index + length);
    text += (unit === '<' || unit === '\\<') && css[index + length] === '/' ? '\\3c ' : unit;
    index += length - 1;
  }
  return text;
}

function lightDocument(parsed: Document): void {
  for (const style of parsed.querySelectorAll('style')) {
    // A style element of another type has no sheet and is never applied.
    if (style.sheet && lightRules(style.sheet.cssRules)) style.textContent = rawTextCss(Array.from(style.sheet.cssRules, rule => rule.cssText).join('\n'));
  }
  // Style elements and picture sources (a mail's dark logo) carry their own media conditions.
  for (const element of parsed.querySelectorAll('[media]')) {
    const media = matchMedia(element.getAttribute('media')!).media;
    if (lightMedia(media) !== media) element.setAttribute('media', lightMedia(media));
  }
}

/** Collapses the frame's viewport for one synchronous read, so content sized from the viewport (vh units,
 *  a `height: 100%` html/body chain) measures as its own extent and cannot grow with every resize. The
 *  wrapper holds the previous height meanwhile, or the host's scroll position would be clamped. */
function fitToContent(frame: HTMLIFrameElement, wrapper: HTMLDivElement, root: HTMLElement): void {
  wrapper.style.minHeight = `${frame.offsetHeight}px`;
  frame.style.height = '0px';
  const height = root.scrollHeight;
  frame.style.height = `${height}px`;
  // A message wider than the frame scrolls sideways; a classic horizontal scrollbar takes its height
  // from the viewport, which would otherwise scroll vertically by that much.
  const scrollbar = frame.contentWindow!.innerHeight - root.clientHeight;
  if (scrollbar > 0) frame.style.height = `${height + scrollbar}px`;
  wrapper.style.minHeight = '';
}

export function SandboxedHtml({ html, allowRemoteImages = false, title }: SandboxedHtmlProps) {
  const [srcDoc, setSrcDoc] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // `allow-same-origin` without `allow-scripts` lets the host read the document's height; nothing inside
  // can run, and the CSP forbids scripts as well. The current document is observed at once and each newly
  // loaded one (new HTML, images allowed) replaces it; the observer reports the first size immediately and
  // every later change (a resize, an opened <details>, a late image).
  useEffect(() => {
    const wrapper = wrapperRef.current!;
    const frame = frameRef.current!;
    let observer: ResizeObserver | null = null;
    const observe = () => {
      observer?.disconnect();
      const root = frame.contentDocument!.documentElement;
      observer = new ResizeObserver(() => fitToContent(frame, wrapper, root));
      observer.observe(root);
    };
    observe();
    frame.addEventListener('load', observe);
    return () => {
      frame.removeEventListener('load', observe);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    // This detached document has no browsing context: its native document parser preserves html/body
    // attributes without loading resources. Never write incoming HTML into the live host document.
    const parsed = document.implementation.createHTMLDocument('');
    parsed.open();
    parsed.write(html);
    parsed.close();
    // Templates are not static message content. Removing them also prevents srcdoc's parser from
    // activating declarative shadow roots containing links or metadata outside these queries. A nested
    // frame never loads a remote page (frame-src 'none') but would render its own srcdoc unprocessed.
    for (const element of parsed.querySelectorAll('meta, base, template, iframe')) element.remove();
    for (const link of parsed.querySelectorAll('a, area')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.removeAttribute('ping');
    }
    // `[*|href]` also matches SVG's namespaced `xlink:href`; both resolve against the base the frame uses.
    for (const element of parsed.querySelectorAll('[*|href]')) {
      for (const attribute of Array.from(element.attributes)) {
        if (attribute.localName !== 'href') continue;
        const allowed = URL.canParse(attribute.value, document.baseURI) && LINK_PROTOCOLS.has(new URL(attribute.value, document.baseURI).protocol);
        if (!allowed) element.removeAttributeNode(attribute);
      }
    }
    lightDocument(parsed);
    parsed.head.insertAdjacentHTML('afterbegin', `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${htmlFrameCsp({ allowRemoteImages })}"><meta name="referrer" content="no-referrer"><base target="_blank">`);
    setSrcDoc(`<!doctype html>${parsed.documentElement.outerHTML}`);
  }, [html, allowRemoteImages]);

  return <div ref={wrapperRef}>
    <iframe ref={frameRef} title={title} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer"
      srcDoc={srcDoc} className="block h-0 w-full border-0 bg-image-ground scheme-light" />
  </div>;
}
